import React, { useState, useEffect, useMemo } from 'react'
import style from './Navigation.module.scss'
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { logout, updateAdminInfo } from '../../../redux/Admin.slice'
import { Form, Input, message } from 'antd'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { FormItem, Input as FormikInput } from 'formik-antd'
import AdminModalV2 from '../../../App/AdminModalV2'
import {
  DownOutlined,
  UpOutlined,
  MenuOutlined,
  AppstoreOutlined,
  SettingOutlined,
  DeploymentUnitOutlined,
  HomeOutlined,
  TeamOutlined,
  LogoutOutlined
} from '@ant-design/icons'
import { usePermission } from '../../../hooks/usePermission'
import { filterNavigationRoutes } from './navigationAccess'

const MENU_PERMISSION = {
  UNIVERSITIES_READ: 'universities.read',
  UNIVERSITIES_READ_PUBLISHED: 'universities.read.published',
  SCHOLARSHIPS_READ: 'scholarships.read',
  DOCUMENTS_READ: 'documents.read',
  SYSTEM_OPTIONS_READ: 'system_options.read',
  LEGACY_MENU_ACCESS: 'legacy_menu.access',
  ADMINS_READ: 'admins.read',
  CRM_READ: 'crm.read',
  ADMISSION_READ: 'admission.read',
}

const _routes = [
  { path: '/admin/v2/home', label: 'Home', icon: <HomeOutlined /> },
  {
    title: 'Information Hub',
    icon: <DeploymentUnitOutlined />,
    children: [
      {
        path: '/admin/v3/university',
        label: 'University',
        anyOf: [MENU_PERMISSION.UNIVERSITIES_READ, MENU_PERMISSION.UNIVERSITIES_READ_PUBLISHED]
      },
      { path: '/admin/v2/scholarships', label: 'Scholarship', permission: MENU_PERMISSION.SCHOLARSHIPS_READ },
      { path: '/admin/v2/documents', label: 'Documents', permission: MENU_PERMISSION.DOCUMENTS_READ },
      { path: '/admin/v2/options-management', label: 'System Options', permission: MENU_PERMISSION.SYSTEM_OPTIONS_READ }
    ]
  },
  {
    title: 'Legacy Modules',
    icon: <AppstoreOutlined />,
    permission: MENU_PERMISSION.LEGACY_MENU_ACCESS,
    children: [
      { path: '/admin/university', label: 'Universities' },
      { path: '/admin/program', label: 'Programs' },
      { path: '/admin/majors', label: 'Majors' },
      { path: '/admin/courses', label: 'Courses' },
      { path: '/admin/users', label: 'Users' },
      { path: '/admin/billings', label: 'Billing' },
      { path: '/admin/blogs', label: 'Blog' },
      { path: '/admin/reviews', label: 'Review' },
      { path: '/admin/homepic', label: 'Banner' },
      { path: '/admin/applications', label: 'Application' },
      { path: '/admin/countries', label: 'Country' },
      // ['/admin/applying', 'Apply'],
      // ['/admin/scholarship', 'Scholarship'],
      // ['/admin/cost', 'Cost'],
      { path: '/admin/english-score', label: 'English Score' },
      { path: '/admin/chinese-score', label: 'Chinese Score' },
      { path: '/admin/questionnaire', label: 'Questionnaire' },
      { path: '/admin/config', label: 'Settings' },
    ]
  },
  {
    title: 'CRM',
    icon: <TeamOutlined />,
    children: [
      { path: '/admin/v2/crm/student-profiles', label: 'Admission Profiles', permission: MENU_PERMISSION.ADMISSION_READ },
      { path: '/admin/v2/crm/member-profiles', label: 'Member Profiles', permission: MENU_PERMISSION.CRM_READ }
    ]
  },
]

const superAdminRoutes = [
  {
    title: 'Admin Setting',
    icon: <SettingOutlined />,
    permission: MENU_PERMISSION.ADMINS_READ,
    children: [
      { path: '/admin/v2/management', label: 'Admin Setting', permission: MENU_PERMISSION.ADMINS_READ },
      { path: '/admin/v2/file-manager', label: 'File Manager', permission: MENU_PERMISSION.ADMINS_READ }
    ]
  }
]

const UpdateProfileModal = ({ visible, onCancel, currentUser }) => {
  const dispatch = useDispatch()

  return (
    <Formik
      initialValues={{
        firstName: currentUser?.firstName || '',
        lastName: currentUser?.lastName || '',
        password: '',
        confirmPassword: ''
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        password: Yup.string().min(6, 'Password must be at least 6 characters'),
        confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match')
      })}
      onSubmit={(values, { setSubmitting }) => {
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName
        }
        if (values.password) {
          payload.password = values.password
        }

        dispatch(updateAdminInfo({ adminId: currentUser?.id, data: payload }))
          .unwrap()
          .then(() => {
            message.success('Profile updated successfully')
            onCancel()
          })
          .catch((err) => {
            message.error(err?.message || 'Failed to update profile')
          })
          .finally(() => {
            setSubmitting(false)
          })
      }}
    >
      {({ handleSubmit, isSubmitting, values, handleBlur, errors, touched, setFieldValue }) => (
        <AdminModalV2
          title="Update Account Information"
          visible={visible}
          onCancel={onCancel}
          onSubmit={handleSubmit}
          confirmLoading={isSubmitting}
          destroyOnClose
          submitText="Save"
        >
          <Form layout="vertical" onFinish={handleSubmit}>
            <FormItem name="firstName" label="First Name" required>
              <FormikInput name="firstName" placeholder="First Name" />
            </FormItem>
            <FormItem name="lastName" label="Last Name" required>
              <FormikInput name="lastName" placeholder="Last Name" />
            </FormItem>

            <Form.Item
              label="New Password"
              validateStatus={errors.password && touched.password ? 'error' : ''}
              help={errors.password && touched.password ? errors.password : null}
            >
              <Input.Password
                name="password"
                placeholder="Leave empty to keep current"
                value={values.password}
                onChange={(e) => setFieldValue('password', e.target.value)}
                onBlur={handleBlur}
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              label="Confirm Password"
              validateStatus={errors.confirmPassword && touched.confirmPassword ? 'error' : ''}
              help={errors.confirmPassword && touched.confirmPassword ? errors.confirmPassword : null}
            >
              <Input.Password
                name="confirmPassword"
                placeholder="Confirm new password"
                value={values.confirmPassword}
                onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                onBlur={handleBlur}
                autoComplete="new-password"
              />
            </Form.Item>
          </Form>
        </AdminModalV2>
      )}
    </Formik>
  )
}


function NavAdmin() {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const auth = useSelector((state) => state.admin.auth)
  const [openMenus, setOpenMenus] = useState({})
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)

  const { permissions } = usePermission();

  const routes = useMemo(() => {
    const activeRoutes = [..._routes, ...superAdminRoutes]

    return filterNavigationRoutes(activeRoutes, permissions)
  }, [permissions])

  // Auto-expand menu based on active route
  const { pathname } = location

  useEffect(() => {
    setOpenMenus((prev) => {
      let changed = false
      const nextOpenMenus = { ...prev }

      routes.forEach((item) => {
        if (!item.path) {
          const { title, children } = item
          const hasActiveChild = children.some(({ path }) => pathname.startsWith(path))

          if (hasActiveChild && !nextOpenMenus[title]) {
            nextOpenMenus[title] = true
            changed = true
          }
        }
      })

      return changed ? nextOpenMenus : prev
    })
  }, [pathname, routes])

  // ... rest of logic

  const handleLogout = () => {
    dispatch(logout())
    localStorage.removeItem('token')
    history.push('/admin/login')
  }

  const toggleMenu = (title) => {
    setOpenMenus(prev => ({
      ...prev,
      [title]: !prev[title]
    }))
  }

  const toggleMobile = () => {
    setMobileOpen(!mobileOpen)
  }

  return (
    <>
      <div className={style.hamburger} onClick={toggleMobile}>
        <MenuOutlined style={{ fontSize: '24px' }} />
      </div>
      <div
        className={`${style.overlay} ${mobileOpen ? style.show : ''}`}
        onClick={() => setMobileOpen(false)}
      />
      <div className={`${style.backGround} ${mobileOpen ? style.mobileOpen : ''}`}>
        <div className={`${style.wrapper} ${style.navLink}`}>
          <div className={style.start}>
            <div className={style.pic}>
              <Link to='/'>
                <img src="/logo.svg" alt='casc-logo' />
              </Link>
            </div>
          </div>
          <div className={style.profile}>
            <div className={style.nameRole}>
              {auth?.firstName !== null && (
                <div className={style.name}>
                  Name : {auth?.firstName} {auth?.lastName}
                </div>
              )}
              {auth?.role !== null && <div className={style.role}>Role : {auth?.role}</div>}

              <div
                className={style.accountSetting}
                onClick={() => setShowProfileModal(true)}
              >
                <SettingOutlined />
                <span>Account setting</span>
              </div>
            </div>
          </div>
          <div className={style.mid}>
            {routes?.map((item, index) => {
              if (item.path) {
                const { path, label, icon } = item
                return (
                  <NavLink
                    key={path}
                    to={path}
                    className={style.navItem}
                    activeClassName={style.active}
                  >
                    <div className={style.linkContent}>
                      {icon && <span className={style.icon}>{icon}</span>}
                      <span>{label}</span>
                    </div>
                  </NavLink>
                )
              } else {
                const { title, children } = item
                const isOpen = openMenus[title]
                return (
                  <div key={title} className={`${style.menuGroup} ${isOpen ? style.open : ''}`}>
                    <div className={style.menuTitle} onClick={() => toggleMenu(title)}>
                      <div className={style.titleContent}>
                        {item.icon && <span className={style.icon}>{item.icon}</span>}
                        <span>{title}</span>
                      </div>
                      <span className={style.arrow}>{isOpen ? <UpOutlined /> : <DownOutlined />}</span>
                    </div>
                    <div className={`${style.subMenu} ${isOpen ? style.subMenuOpen : ''}`}>
                      {children.map(({ path, label }) => (
                        <NavLink
                          key={path}
                          to={path}
                          className={style.subMenuItem}
                          activeClassName={style.activeSub}
                        >
                          {label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )
              }
            })}
          </div>
          <div className={style.end}>
            <div className={style.logoutBtn} onClick={() => handleLogout()}>
              <span className={style.icon}><LogoutOutlined /></span>
              <span>Log Out</span>
            </div>
          </div>
        </div>
      </div>
      <UpdateProfileModal
        visible={showProfileModal}
        onCancel={() => setShowProfileModal(false)}
        currentUser={auth}
      />
    </>
  )
}

export default NavAdmin
